import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatTabsModule } from '@angular/material/tabs';
import { CanvasControllerService } from '../../../../../../services/canvas-controller/canvas-controller.service';
import { StickerListControllerService } from '../../services/sticker-list-controller/sticker-list-controller.service';
import { Subject, takeUntil, map, take, lastValueFrom } from 'rxjs';
import { LiveryAttachToolComponent } from '../sticker-attach-tool/livery-attach-tool.component';
import { LiveryEditorToolComponent } from '../sticker-editor-tool/livery-editor-tool.component';
import { StickerData } from '../../services/sticker-list-controller/sticker-list-controller';
import { ThreeGraphqlService } from 'src/app/core/graphql/three/services/three-graphql.service';
import { LiveryAttachStickerListComponent } from '../sticker-attach-list/livery-attach-sticker-list.component';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { LayoutService } from '../../../../../layout.service';

@Component({
  selector: 'sticker-tool',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatGridListModule,
    MatTabsModule,
    LiveryAttachToolComponent,
    LiveryEditorToolComponent,
    LiveryAttachStickerListComponent,
  ],
  templateUrl: './sticker-tool.component.html',
  styleUrl: './sticker-tool.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StickerToolComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('mainDom') mainDom: ElementRef<HTMLDivElement> | undefined;

  public isLoading: boolean = true;
  public isDragOver: boolean = false;

  public layoutDirection: 'horizontal' | 'vertical' = 'horizontal';
  public width: number = 150;

  public selectedMakeStickerTypeIndex: number = 0;
  public selectedStickerListTypeIndex: number = 0;

  public attachStickers: StickerData[] = [];
  public defaultStickers: StickerData[] = [];

  public attachSticker: StickerData | undefined;

  public selectedAttachedStickerIndex: number = -1;

  public promptForm: FormControl = new FormControl('', [Validators.required]);

  private resizeObserver: ResizeObserver | undefined;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    private layout: LayoutService,
    private canvasController: CanvasControllerService,
    private threeGraphqlService: ThreeGraphqlService,
    private stickerListController: StickerListControllerService,
    private cookie: CookieService,
    private cd: ChangeDetectorRef,
  ) {}

  get historyStickers(): StickerData[] {
    return this.cookie.get('historyStickers') ? JSON.parse(this.cookie.get('historyStickers')) : [];
  }

  set historyStickers(stickers: StickerData[]) {
    this.cookie.set('historyStickers', JSON.stringify(stickers));
  }

  //--------------------------------------------------------------------------------
  // @ Lifecycle hooks
  //--------------------------------------------------------------------------------

  ngOnInit(): void {
    this.getAttachStickers();
    this.getStickerList();
    this.layout.dragDropFile$.pipe(takeUntil(this._unsubscribeAll)).subscribe((file) => {
      if (!file) return;
      this.layout.isLoading = true;
      this.threeGraphqlService
        .uploadImage(file)
        .pipe(take(1))
        .subscribe((imageUrl: string) => {
          console.log('imageUrl', imageUrl);
          this.canvasController.createStickerView(imageUrl, 'sticker').then(() => {
            this.attachSticker = {
              title: `Untitle Sticker ${this.stickerListController.stickerCount}`,
              type: 'sticker',
              image: imageUrl,
              thumnail: imageUrl,
            };
            const historyStickers = [...this.historyStickers, this.attachSticker];
            this.historyStickers = historyStickers;

            this.layout.isLoading = false;
            this.cd.markForCheck();
          });
        });
    });
  }

  ngAfterViewInit(): void {
    this.observeResize();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
    this._unsubscribeAll.unsubscribe();
  }

  //--------------------------------------------------------------------------------
  // @ Public methods
  //--------------------------------------------------------------------------------

  public onSendAi(): void {
    if (this.promptForm.invalid) return;
    this.layout.isLoading = true;
    this.threeGraphqlService
      .transKoToEn(this.promptForm.value)
      .pipe(take(1))
      .subscribe((transText: string) => {
        lastValueFrom(this.threeGraphqlService.generateLivery(transText)).then(
          (liveryImgUrl: string) => {
            this.canvasController.createStickerView(liveryImgUrl, 'livery').then(() => {
              this.attachSticker = {
                title: `Untitle Sticker ${this.stickerListController.stickerCount}`,
                type: 'livery',
                image: liveryImgUrl,
                thumnail: liveryImgUrl,
              };
              const historyStickers = [...this.historyStickers, this.attachSticker];
              this.historyStickers = historyStickers;
              this.layout.isLoading = false;
              this.cd.markForCheck();
            });
            this.layout.isLoading = false;
            this.cd.markForCheck();
          },
        );
      });
    this.cd.markForCheck();
  }

  public onClickSelectSticker(sticker: StickerData): void {
    this.canvasController.createStickerView(sticker.image, sticker.type).then(() => {
      console.log('sticker.image', sticker);

      const attachSticker: StickerData = {
        title: `Untitle Sticker ${this.stickerListController.stickerCount}`,
        type: sticker.type,
        image: sticker.image,
        thumnail: sticker.thumnail,
      };
      this.attachSticker = attachSticker;
      this.cd.markForCheck();
    });
  }

  public initAttachSticker(): void {
    this.attachSticker = undefined;
    this.cd.markForCheck();
  }

  public onEditAttachSticker(index: number): void {
    this.selectedAttachedStickerIndex = index;
    this.cd.markForCheck();
  }

  public initEditor(): void {
    this.selectedAttachedStickerIndex = -1;
    this.cd.markForCheck();
  }

  public inputImageFile(event: any): void {
    const file = event.target.files[0];
    if (!file) return;
    this.layout.isLoading = true;
    this.threeGraphqlService
      .uploadImage(file)
      .pipe(take(1))
      .subscribe((imageUrl: string) => {
        this.canvasController.createStickerView(imageUrl, 'sticker').then(() => {
          this.attachSticker = {
            title: `Untitle Sticker ${this.stickerListController.stickerCount}`,
            type: 'sticker',
            image: imageUrl,
            thumnail: imageUrl,
          };
          const historyStickers = [...this.historyStickers, this.attachSticker];
          this.historyStickers = historyStickers;
          event.target.value = '';
          event.target.files = null;
          this.layout.isLoading = false;
          this.cd.markForCheck();
        });
      });
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
    this.cd.markForCheck();
  }

  public onDragOut(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    this.cd.markForCheck();
  }

  public onDrop(event: DragEvent): void {
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;
    event.preventDefault();
    event.stopPropagation();

    const file = files[0];
    this.layout.isLoading = true;
    this.threeGraphqlService
      .uploadImage(file)
      .pipe(take(1))
      .subscribe((imageUrl: string) => {
        this.canvasController.createStickerView(imageUrl, 'sticker').then(() => {
          this.attachSticker = {
            title: `Untitle Sticker ${this.stickerListController.stickerCount}`,
            type: 'sticker',
            image: imageUrl,
            thumnail: imageUrl,
          };
          const historyStickers = [...this.historyStickers, this.attachSticker];
          this.historyStickers = historyStickers;
          this.layout.isLoading = false;
          this.cd.markForCheck();
        });
      });
  }

  //--------------------------------------------------------------------------------
  // @ Private methods
  //--------------------------------------------------------------------------------

  private getAttachStickers(): void {
    this.stickerListController.attachStickers$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((attachStickers) => {
        this.attachStickers = attachStickers;
        console.log('attachStickers', attachStickers);
        this.cd.detectChanges();
      });
  }

  private getStickerList(): void {
    this.layout.isLoading = true;
    this.threeGraphqlService
      .getStickers()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((stickerList) => {
        this.defaultStickers = stickerList.map((sticker: any) => {
          const data: StickerData = {
            title: sticker.title,
            type: sticker.type,
            image: sticker.stickerFile,
            thumnail: sticker.thumbnail,
          };

          return data;
        });
        this.layout.isLoading = false;

        this.cd.markForCheck();
      });
  }

  private observeResize(): void {
    if (!this.mainDom) return;
    this.resizeObserver = new ResizeObserver(() => {
      if (!this.mainDom) return;
      this.width = this.mainDom?.nativeElement.clientWidth;

      this.cd.markForCheck();
    });

    this.resizeObserver.observe(this.mainDom.nativeElement);
  }
}
